import { Button, Notice, Stack } from '@eleveo/components-react';
import { BinIcon } from '@eleveo/icons-react';
import { WithT } from 'i18next';
import React, { useEffect, useState } from 'react';
import { FormProps } from 'react-final-form';

import { SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { SidebarForm } from '../../components/LayoutNext/SidebarForm';
import { SkillWithUsed } from '../../graphql/components';
import { SkillForm } from './SkillForm';
import { Values } from './SkillsScene';
import { DELETE_SKILL_BUTTON_TAG, DELETE_SKILL_NOTICE_TAG, SAVE_SKILL_BUTTON_TAG } from './constants';

type SkillsSidebarProps = {
  handleCancel: () => void;
  handleShowDeleteModal: (skill: SkillWithUsed) => void;
  handleSubmit: FormProps<Values>['onSubmit'];
  skill: SkillWithUsed | null;
} & WithT;

const initialSkill = {
  id: '',
  name: '',
};

export const normalizeSkill = (skill: SkillWithUsed) => ({
  id: skill.id,
  name: skill.name,
});

export const SkillsSidebar: React.FC<SkillsSidebarProps> = ({
  handleCancel,
  handleShowDeleteModal,
  handleSubmit,
  skill,
  t,
}) => {
  const [initialValues, setInitialValues] = useState<Values>(initialSkill);

  useEffect(() => {
    if (skill) {
      setInitialValues(normalizeSkill(skill));
    } else {
      setInitialValues(initialSkill);
    }
  }, [skill]);

  const handleDelete = (s: SkillWithUsed) => () => handleShowDeleteModal(s);

  return (
    <SidebarForm<Values>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onClose={handleCancel}
      title={skill ? t('skill.edit') : t('skill.add')}
    >
      {({ pristine, invalid }) => (
        <>
          <SidebarPanel>
            <Stack direction="horizontal" className="w-full">
              <Button color="success" isDisabled={pristine || invalid} testId={SAVE_SKILL_BUTTON_TAG} type="submit">
                {t('layout.action.save')}
              </Button>
              {skill && (
                <Button
                  color="error"
                  isDisabled={skill.used}
                  onClick={handleDelete(skill)}
                  testId={DELETE_SKILL_BUTTON_TAG}
                  variant="outline"
                >
                  <BinIcon />
                  {t('layout.action.delete')}
                </Button>
              )}
            </Stack>
          </SidebarPanel>

          <SidebarContent>
            <SidebarPart>
              <Stack direction="vertical" className="p-20 w-full" spacing={20}>
                {skill?.used && <Notice testId={DELETE_SKILL_NOTICE_TAG}>{t('skill.cannotBeDeleted')}</Notice>}
                <SkillForm t={t} />
              </Stack>
            </SidebarPart>
          </SidebarContent>
        </>
      )}
    </SidebarForm>
  );
};
